import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { SortingRule } from 'react-table'
import { faCommentAltEdit } from '@fortawesome/pro-regular-svg-icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { useInView } from 'react-intersection-observer'
import flatten from 'lodash/flatten'
import isEqual from 'lodash/isEqual'

import { Experience, ExperienceFilter } from '../types/experiences'
import { useParamsToObj } from '../hooks/useParamsToObj'
import { useExperiences } from '../hooks/useExperiences'

import PageHeader from '../components/PageHeader'
import ErrorPage from '../components/ErrorPage'
import TableSkeleton from '../components/TableSkeleton'
import EmptyContent from '../components/EmptyContent'
import FilterDrawer from '../components/Experiences/FilterDrawer'
import Spinner from '../components/Spinner'
import ExperiencesTable from '../components/Experiences/ExperiencesTable'

const initialFilter = {
  date_from: '',
  date_to: '',
  rating: [],
  status: null,
  customers: [],
  locations: [],
  areas: [],
  usernames: [],
}

const Experiences: React.FC = () => {
  const urlParams = useParamsToObj(['search'])
  const location = useLocation()
  const navigate = useNavigate()

  const [filter, setFilter] = useState<ExperienceFilter>(initialFilter)
  const [orderByFilter, setOrderBy] = useState<SortingRule<Experience>[]>([])
  const [filterDrawerStatus, setFilterDrawerStatus] = useState(false)

  const experiences = useExperiences(urlParams, filter, orderByFilter)
  const { hasNextPage, fetchNextPage } = experiences

  const { ref, inView } = useInView({
    rootMargin: '100px 0px 0px 0px',
  })

  useEffect(() => {
    const init = async () => {
      if (inView && hasNextPage) {
        await fetchNextPage()
      }
    }
    init()
  }, [inView, hasNextPage, fetchNextPage])

  // Handle react-table sorting changes (in Table.tsx)
  const handleSortByChange = useCallback(
    (orderBy: SortingRule<any>[]) => {
      if (isEqual(orderByFilter, orderBy)) return

      setOrderBy(orderBy)
    },
    [orderByFilter]
  )

  const experiencesData = flatten(experiences.data?.pages)
  const experiencesMemoData = useMemo(() => experiencesData || [], [experiencesData])

  const resetFilters = () => {
    setFilter(initialFilter)
    navigate({
      search: ``,
    })
  }

  const filterCount = (): number => {
    let counter = 0

    if (filter.date_from !== '') counter++
    if (filter.date_to !== '') counter++
    if (filter.rating.length > 0) counter++
    if (filter.status) counter++
    if (filter.customers.length > 0) counter++
    if (filter.locations.length > 0) counter++
    if (filter.areas.length > 0) counter++
    if (filter.usernames.length > 0) counter++

    return counter
  }

  if (experiences.isLoading) {
    return (
      <>
        <PageHeader
          title="Experiences"
          filterBtnAction={() => {}}
          urlParams={urlParams}
          searchPlaceholder="Search Experience Title"
          nbOfExtraFilter={filterCount()}
        />
        <TableSkeleton headers={['Title & User', 'Location & Area', 'Customer', 'Rating', 'Status', 'Date', ' ']} />
      </>
    )
  }

  if (experiences.isError) {
    return (
      <ErrorPage
        title="Something went wrong!"
        text="We keep track of these errors, but feel free to contact us if refreshing doesn't fix things."
      />
    )
  }

  if ((!experiences.data || experiencesData?.length < 1) && (location.search || filterCount() > 0)) {
    return (
      <div className="mt-32 flex h-full items-center justify-center">
        <EmptyContent
          title={`No results found`}
          description="Please change the search terms to match results."
          icon={faCommentAltEdit}
          btnAction={resetFilters}
          btnText="Reset filters"
        />
      </div>
    )
  }

  if (!experiences.data || experiencesData?.length < 1) {
    return (
      <div className="mt-32 flex h-full items-center justify-center">
        <EmptyContent title="No Experiences found" description="Start by adding a new one." icon={faCommentAltEdit} />
      </div>
    )
  }

  return (
    <div>
      <FilterDrawer
        open={filterDrawerStatus}
        onClose={() => setFilterDrawerStatus(false)}
        filter={filter}
        setFilter={setFilter}
        resetFilter={resetFilters}
      />

      <PageHeader
        title="Experiences"
        filterBtnAction={() => setFilterDrawerStatus(true)}
        urlParams={urlParams}
        searchPlaceholder="Search Experience Title"
        nbOfExtraFilter={filterCount()}
      />

      <ExperiencesTable onSortBy={handleSortByChange} data={experiencesMemoData} />

      {hasNextPage ? (
        <div ref={ref} className="mt-4 mb-4 flex flex-col items-center text-center">
          <Spinner className="h-8 w-8 text-gray-400" />
          <span className="text-gray-400">Loading more...</span>
        </div>
      ) : null}
    </div>
  )
}

export default Experiences
